import { globalHistory, HistoryListenerParameter } from '@reach/router';
import { useEffect, useState } from 'react';
import FloatBox from './FloatBox';
import { connect, MapStateToProps } from 'react-redux';
import { IGuide } from 'store/siteGuide/SiteGuideSlice';
import './style.scss';
import { convertDigitToProperFormat } from 'utils/number';
import message from './messages';
import { RootState } from 'store/store';
import { languageDirection } from '../../i18n';
import styled from 'styled-components';
import { blue, exactWhite } from '../../utils/style/colors';
import LocalIcon from '../../ui/LocalIcon';
import { getDomainAndPort } from '../../api/Utils';

const FloatBoxContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 1rem;
  align-items: center;
  position: fixed;
  left: 1.5rem;
  bottom: 1.5rem;
`;

const AICircle = styled.div`
  position: relative;
  border-radius: 10rem;
  height: 3.5rem;
  width: 3.5rem;
  //background: ${blue};
  background: #3f5efb;
  background: linear-gradient(
    96deg,
    rgb(66, 97, 253) 0%,
    rgb(244, 75, 109) 100%
  );
  /* box-shadow:
    var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
    var(--tw-shadow); */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row-reverse;
  gap: 0.5rem;
  color: ${exactWhite};
  @media print {
    display: none;
  }

  transition: width 0.25s ease-in-out;

  ${(props) =>
    props.isHoverEnabled &&
    `
    width: 10rem;
    border-radius: 10rem;
  `}
  &:hover {
    cursor: pointer;
  }
`;

const AIContentContainer = styled.div`
  text-wrap: nowrap;
`;

interface IStateToProps {
  allGuides: Array<IGuide>;
  relatedGuideKeys: Array<string>;
  toViewGuide?: string;
}

type IBotConfiguration = {
  hide?: boolean;
  botLink: string;
  botText: string;
};

const blackListPathKeywords = ['conversation', '/print/'];

const defaultBotConfiguration: IBotConfiguration = {
  botLink: 'https://app.rastech.ir/help#chat',
  botText: 'همیار هاسپیتک',
};

const botConfiguration =
  getDomainAndPort().domainFeatures?.botConfiguration ??
  defaultBotConfiguration;

const GuideFloatBox = (props: IStateToProps) => {
  const { allGuides, relatedGuideKeys, toViewGuide } = props;
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [isHovered, setIsHovered] = useState<'tutor' | 'ai' | null>(null);
  const [badgeCount, setBadgeCount] = useState(0);
  const [relatedGuides, setRelatedGuides] = useState<Array<IGuide>>([]);
  const [mustHide, setMustHide] = useState(false);

  useEffect(() => {
    if (toViewGuide) setIsPopUpOpen(true);
  }, [toViewGuide]);
  const clickAwayListener = (event: any) => {
    if (event.target.classList.contains('pop-up')) {
      setIsPopUpOpen(false);
    }
  };

  function checkForBlackList(hist: HistoryListenerParameter | Window) {
    const hasBlackItem = blackListPathKeywords.some((itm) =>
      hist.location.pathname.includes(itm)
    );
    setMustHide(hasBlackItem);
  }

  useEffect(() => {
    const filteredRelatedGuides = allGuides.filter((itm) => {
      return relatedGuideKeys.findIndex((key) => key === itm.key) !== -1;
    });
    setRelatedGuides(filteredRelatedGuides);
    const relatedGuidesBadgeCount = filteredRelatedGuides.filter(
      (guide) => !guide.isWatched
    ).length;
    setBadgeCount(relatedGuidesBadgeCount);
  }, [relatedGuideKeys, allGuides]);

  globalHistory.listen(checkForBlackList);

  useEffect(() => {
    checkForBlackList(window);
  }, [window.location.pathname]);

  return (
    <div
      onClick={clickAwayListener}
      style={{ zIndex: 100000000 }}
      className={`pop-up ${allGuides.length > 0 ? '--has-guide' : ''} ${isPopUpOpen ? '--open' : ''} ${mustHide ? 'hidden' : 'block'}`.trim()}
    >
      {isPopUpOpen && (
        <FloatBox
          allGuides={allGuides}
          relatedGuides={relatedGuides}
          activeGuide={toViewGuide}
        />
      )}
      <FloatBoxContainer>
        {!getDomainAndPort()?.domainFeatures?.hideTutorials && (
          <div
            className={`pop-up__button ${
              isHovered === 'tutor' && !isPopUpOpen ? '--hovered' : ''
            } ${badgeCount > 0 ? '--has-related' : ''} ${mustHide ? 'hidden' : 'flex'}`.trim()}
            onMouseEnter={() => setIsHovered('tutor')}
            onMouseLeave={() => setIsHovered(null)}
            onClick={() => setIsPopUpOpen((prev) => !prev)}
            style={{
              left: languageDirection() === 'rtl' ? '0' : 'initial',
              right: languageDirection() === 'rtl' ? 'initial' : '0',
              bottom: '0',
              position: 'relative',
            }}
          >
            {!isHovered && !isPopUpOpen && badgeCount > 0 && (
              <div className="pop-up__button__badge">
                {convertDigitToProperFormat(badgeCount)}
              </div>
            )}
            {isPopUpOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
              >
                <path
                  d="M21 7L7 21M7 7L21 21"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <>
                {isHovered === 'tutor' && badgeCount > 0 && (
                  <>
                    <span>{message.relatedGuideCount(badgeCount)}</span>
                  </>
                )}
                {bookIcon}
              </>
            )}
          </div>
        )}
        {!botConfiguration.hide && (
          <a href={botConfiguration.botLink} target={'_blank'} rel="noreferrer">
            <AICircle
              onMouseEnter={() => setIsHovered('ai')}
              onMouseLeave={() => setIsHovered(null)}
              isHoverEnabled={isHovered === 'ai'}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '0.5',
                  left: '0.7rem',
                  position: 'absolute',
                  flexDirection: 'row-reverse',
                  alignItems: 'center',
                }}
              >
                <LocalIcon
                  iconName={'ai-bot.svg'}
                  color={exactWhite}
                  onClick={() => {
                    // unused function
                  }}
                />
                {isHovered === 'ai' && (
                  <AIContentContainer>
                    {' '}
                    {botConfiguration.botText as string}
                  </AIContentContainer>
                )}
              </div>
            </AICircle>
          </a>
        )}
      </FloatBoxContainer>
    </div>
  );
};

const mapStateToProps: MapStateToProps<
  IStateToProps,
  Record<string, unknown>,
  RootState
> = (state) => {
  const allGuides = state.siteGuide.all;
  const relatedGuideKeys = state.siteGuide.relatedGuideKeys;
  const toViewGuide = state.siteGuide.toViewGuide;

  return {
    allGuides,
    relatedGuideKeys,
    toViewGuide,
  };
};

export default connect(mapStateToProps, null)(GuideFloatBox);

const bookIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.0001 24.5H7.00008C5.71091 24.5 4.66675 23.4558 4.66675 22.1667V5.83333C4.66675 4.54417 5.71091 3.5 7.00008 3.5H21.0001C22.2892 3.5 23.3334 4.54417 23.3334 5.83333V22.1667C23.3334 23.4558 22.2892 24.5 21.0001 24.5Z"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 9.33333H6.21833"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 14.0001H6.21833"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.36011 18.6666H6.21844"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.33333 3.5V24.5"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 9.33333H18.6667"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 14.0001H18.6667"
      stroke="#F2F5F7"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
